import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Calculator, PoundSterling, Target, Calendar, Gift, AlertCircle, CheckCircle, HelpCircle, PieChart } from 'lucide-react';

const SippCalculatorPage = () => {
  const navigate = useNavigate();

  // Calculator state
  const [age, setAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentSippValue, setCurrentSippValue] = useState(50000);
  const [monthlyContribution, setMonthlyContribution] = useState(500);
  const [expectedReturn, setExpectedReturn] = useState(5);
  const [employerContribution, setEmployerContribution] = useState(0);

  // Calculate SIPP projections
  const calculateSipp = () => {
    const yearsToRetirement = retirementAge - age;
    const annualContribution = (monthlyContribution + employerContribution) * 12;
    const returnRate = expectedReturn / 100;

    // Calculate tax relief (basic rate 20%)
    const taxRelief = annualContribution * 0.25; // 20% relief = 25% gross up
    const totalAnnualContribution = annualContribution + taxRelief;

    // Future value calculation with compound interest
    let futureValue = currentSippValue;
    let totalContributed = 0;
    let totalTaxRelief = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      futureValue = futureValue * (1 + returnRate) + totalAnnualContribution;
      totalContributed += annualContribution;
      totalTaxRelief += taxRelief;
    }

    // Calculate 25% tax-free lump sum
    const taxFreeLumpSum = futureValue * 0.25;
    const remainingPension = futureValue - taxFreeLumpSum;

    // Estimate annual drawdown (4% rule)
    const annualDrawdown = remainingPension * 0.04;
    const monthlyIncome = annualDrawdown / 12;

    return {
      futureValue: Math.round(futureValue),
      totalContributed: Math.round(totalContributed),
      totalTaxRelief: Math.round(totalTaxRelief),
      taxFreeLumpSum: Math.round(taxFreeLumpSum),
      remainingPension: Math.round(remainingPension),
      annualDrawdown: Math.round(annualDrawdown),
      monthlyIncome: Math.round(monthlyIncome),
      yearsToRetirement
    };
  };

  const results = calculateSipp();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens AI</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
            <PoundSterling className="w-4 h-4" />
            <span className="text-sm font-semibold">UK SIPP Planning Tool</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            SIPP Calculator
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> for UK</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8">
            Plan your Self-Invested Personal Pension with tax relief calculations, retirement projections,
            and income estimates. Free, accurate, and easy to use.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Input Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <Calculator className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Your SIPP Details</h2>
            </div>

            <div className="space-y-6">
              {/* Current Age */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Age
                </label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="18"
                  max="75"
                />
              </div>

              {/* Retirement Age */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Target Retirement Age
                </label>
                <input
                  type="number"
                  value={retirementAge}
                  onChange={(e) => setRetirementAge(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="55"
                  max="75"
                />
              </div>

              {/* Current SIPP Value */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current SIPP Value (£)
                </label>
                <input
                  type="number"
                  value={currentSippValue}
                  onChange={(e) => setCurrentSippValue(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="1000"
                />
              </div>

              {/* Monthly Personal Contribution */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Personal Contribution (£)
                </label>
                <input
                  type="number"
                  value={monthlyContribution}
                  onChange={(e) => setMonthlyContribution(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="50"
                />
              </div>

              {/* Monthly Employer Contribution */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Employer Contribution (£)
                </label>
                <input
                  type="number"
                  value={employerContribution}
                  onChange={(e) => setEmployerContribution(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="50"
                />
              </div>

              {/* Expected Annual Return */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Expected Annual Return (%)
                </label>
                <input
                  type="number"
                  value={expectedReturn}
                  onChange={(e) => setExpectedReturn(parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max="15"
                  step="0.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Conservative: 4-5% | Moderate: 6-7% | Aggressive: 8-10%
                </p>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Projected SIPP Value */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6" />
                <h3 className="text-xl font-bold">Projected SIPP Value at {retirementAge}</h3>
              </div>
              <div className="text-5xl font-bold mb-2">
                £{results.futureValue.toLocaleString()}
              </div>
              <p className="text-blue-100">
                In {results.yearsToRetirement} years
              </p>
            </div>

            {/* Tax-Free Lump Sum */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-200">
              <div className="flex items-center space-x-3 mb-3">
                <Gift className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">Tax-Free Lump Sum (25%)</h3>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                £{results.taxFreeLumpSum.toLocaleString()}
              </div>
              <p className="text-sm text-gray-600">
                Tax-free cash you can withdraw at retirement
              </p>
            </div>

            {/* Monthly Retirement Income */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Estimated Monthly Income</h3>
              </div>
              <div className="text-3xl font-bold text-blue-600 mb-1">
                £{results.monthlyIncome.toLocaleString()}/mo
              </div>
              <p className="text-sm text-gray-600">
                Based on 4% annual drawdown (£{results.annualDrawdown.toLocaleString()}/year)
              </p>
            </div>

            {/* Contribution Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contribution Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Your Contributions</span>
                  <span className="font-semibold text-gray-900">£{results.totalContributed.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tax Relief (20%)</span>
                  <span className="font-semibold text-green-600">+£{results.totalTaxRelief.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Investment Growth</span>
                  <span className="font-semibold text-blue-600">
                    £{(results.futureValue - results.totalContributed - results.totalTaxRelief).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SIPP Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* What is a SIPP? */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What is a SIPP?</h2>
            <p className="text-gray-700 mb-4">
              A Self-Invested Personal Pension (SIPP) is a type of UK personal pension that gives you
              greater control over your retirement investments. Unlike traditional pensions managed by
              providers, a SIPP allows you to choose where your money is invested.
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-gray-900">Tax Relief:</span>
                  <span className="text-gray-700"> Get 20% basic rate relief automatically, higher rates via self-assessment</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-gray-900">Investment Choice:</span>
                  <span className="text-gray-700"> Invest in stocks, bonds, funds, ETFs, and more</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-gray-900">25% Tax-Free:</span>
                  <span className="text-gray-700"> Take up to 25% as a tax-free lump sum at retirement</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-gray-900">Access from 55:</span>
                  <span className="text-gray-700"> Access your pension from age 55 (rising to 57 in 2028)</span>
                </div>
              </div>
            </div>
          </div>

          {/* SIPP Contribution Limits */}
          <div className="bg-blue-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SIPP Contribution Limits (2024/25)</h2>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <PoundSterling className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Annual Allowance</h3>
                    <p className="text-sm text-gray-700">
                      £60,000 per year (including tax relief) or 100% of earnings, whichever is lower
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Tapered Allowance</h3>
                    <p className="text-sm text-gray-700">
                      If you earn over £260,000, your allowance reduces (minimum £10,000)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Carry Forward</h3>
                    <p className="text-sm text-gray-700">
                      Use unused allowances from previous 3 tax years (if you were a member)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Lifetime Allowance</h3>
                    <p className="text-sm text-gray-700">
                      Abolished from April 2024—no limit on pension pot size
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SIPP vs Workplace Pension */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">SIPP vs Workplace Pension</h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-blue-600">SIPP</th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-gray-500">Workplace Pension</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Investment Control</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Full control</td>
                  <td className="px-6 py-4 text-center text-gray-600">Limited options</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Employer Contributions</td>
                  <td className="px-6 py-4 text-center text-red-600 font-semibold">No</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Yes (typically 3-5%)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Investment Range</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Thousands of options</td>
                  <td className="px-6 py-4 text-center text-gray-600">10-30 funds</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Annual Fees</td>
                  <td className="px-6 py-4 text-center text-gray-600">0.25% - 0.6%</td>
                  <td className="px-6 py-4 text-center text-gray-600">0.5% - 1%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Consolidation</td>
                  <td className="px-6 py-4 text-center text-green-600 font-semibold">Can consolidate old pensions</td>
                  <td className="px-6 py-4 text-center text-red-600 font-semibold">Usually not possible</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Best For</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Active investors, consolidation</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Employer matching</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 rounded-lg p-4 flex items-start space-x-3">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Pro tip:</span> Many people use both—maximizing employer contributions
              in a workplace pension while maintaining a SIPP for additional savings and consolidating old pensions.
            </p>
          </div>
        </div>

        {/* Track Your SIPP with PlanLens */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <PieChart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Track Your SIPP with PlanLens
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              This calculator gives you a snapshot. PlanLens gives you the complete picture with
              portfolio tracking, AI insights, and retirement planning tools.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Track Multiple SIPPs</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">AI-Powered Insights</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Retirement Projections</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
            >
              Start Tracking Your SIPP Free
            </button>
          </div>
        </div>

        {/* Important Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Important Disclaimer</h3>
              <p className="text-sm text-gray-700 mb-2">
                This calculator provides estimates only and should not be considered financial advice.
                Actual returns will vary based on investment performance, fees, and market conditions.
              </p>
              <p className="text-sm text-gray-700">
                For personalized SIPP advice and planning, please consult an Independent Financial Adviser (IFA)
                regulated by the Financial Conduct Authority (FCA).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © 2025 PlanLens. SIPP Calculator for UK retirement planning.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SippCalculatorPage;
